/*
 * Where Firm Knowledge lives.
 *
 *   /firm-knowledge                    the Library (All Documents)
 *   /firm-knowledge?folder=<id>|unfiled  the Library open on one folder, or on Unfiled
 *   /firm-knowledge?entry=<id>         the Library with one entry's drawer open
 *   /firm-knowledge?tab=intake_templates        the firm's intake templates
 *   /firm-knowledge?tab=intake_templates&template=<id>  one template open in the editor
 *     (a saved template's id, or a catalog id such as a visa starter)
 *
 * Skills moved to Automations (`/automations`, see
 * `features/automations/routes.ts`); `?tab=skills` URLs redirect there.
 *
 * The retired Studio (`/studio`, `/studio/templates`, `/studio/templates/edit/:id`)
 * redirects here; a template id becomes `?entry=` since the migration keeps
 * legacy ids as `source_ref`, not as the new entry id, so the drawer looks the
 * entry up by either. The retired `?tab=instructions` simply lands on the
 * library; the firm's instructions moved to Settings > Case Preparation. No
 * React or IO here so the rules stay unit-testable.
 */
import { matchPath } from "react-router-dom";

export const FIRM_KNOWLEDGE_PATH = "/firm-knowledge";

export type FirmKnowledgeTab = "library" | "intake_templates";

export const FIRM_KNOWLEDGE_TABS: readonly FirmKnowledgeTab[] = [
  "library",
  "intake_templates"
];

export const firmKnowledgePath = (options?: {
  entryId?: string | null;
  /** The Library's open folder, or `unfiled`; All Documents is left out. */
  folderId?: string | null;
  /** An intake template open in the editor. */
  templateId?: string | null;
  tab?: FirmKnowledgeTab | null;
}): string => {
  const params = new URLSearchParams();
  if (options?.tab === "intake_templates" || options?.templateId) {
    params.set("tab", "intake_templates");
    if (options?.templateId) params.set("template", options.templateId);
    return `${FIRM_KNOWLEDGE_PATH}?${params.toString()}`;
  }
  if (options?.folderId && options.folderId !== "all")
    params.set("folder", options.folderId);
  if (options?.entryId) params.set("entry", options.entryId);
  const query = params.toString();
  return query ? `${FIRM_KNOWLEDGE_PATH}?${query}` : FIRM_KNOWLEDGE_PATH;
};

/** Which tab a Firm Knowledge search string asks for (`library` by default). */
export const firmKnowledgeTab = (search: string): FirmKnowledgeTab => {
  const tab = new URLSearchParams(search).get("tab");
  return tab === "intake_templates" ? tab : "library";
};

/** The Library's open folder id, `unfiled`, or `all` (the default). */
export const libraryFolderOf = (search: string): string =>
  new URLSearchParams(search).get("folder")?.trim() || "all";

export const isFirmKnowledgePath = (pathname: string): boolean =>
  matchPath({ path: FIRM_KNOWLEDGE_PATH, end: false }, pathname) !== null;

/**
 * Where a retired Studio URL should land. `/studio/templates/edit/abc` opens
 * the library with `abc` requested; everything else opens the library.
 */
export const studioRedirectTarget = (pathname: string): string => {
  const edit = matchPath(
    { path: "/studio/templates/edit/:templateId", end: true },
    pathname
  );
  const templateId = edit?.params.templateId;
  return firmKnowledgePath({ entryId: templateId ?? null });
};
